import { Avatar, Button, Card, Field, Input, LoadingState, Logo, useLocale } from '@nolens/ui';
import { useState, type FormEvent } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { DEV_LOGIN_ENABLED, DEV_USERS, devSignIn } from '../lib/devAuth';
import { markSignedIn } from '../lib/extension';
import { useSession } from '../lib/session';
import { auth } from '../lib/supabase';
import { authText } from '../i18n/auth';
import { publicPaths } from '../i18n/landing';

type Step = { kind: 'email' } | { kind: 'code'; email: string };

/** Só aceita destinos dentro da app (evita redirecionar para fora depois de entrar). */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/inicio';
}

/** Entrar com Google ou com um código por email; a conta cria-se no primeiro login. */
export function LoginPage() {
  const { session } = useSession();
  const locale = useLocale();
  const t = authText(locale);
  const paths = publicPaths(locale);
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [step, setStep] = useState<Step>({ kind: 'email' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  if (session) return <Navigate to={next} replace />;
  if (DEV_LOGIN_ENABLED)
    return <Navigate to={`/dev/entrar?next=${encodeURIComponent(next)}`} replace />;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  const google = () =>
    void run(async () => {
      markSignedIn();
      const { error } = await auth!.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/entrar?next=${encodeURIComponent(next)}`,
        },
      });
      if (error) setError(t.googleError);
    });

  // Cada pedido novo invalida o código anterior: só vale o do email mais recente.
  const requestCode = (address: string) =>
    run(async () => {
      const { error } = await auth!.signInWithOtp({
        email: address,
        options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}${next}` },
      });
      if (error) {
        setError(error.status === 429 ? t.tooSoon : t.sendError);
        return;
      }
      setCode('');
      setStep({ kind: 'code', email: address });
    });

  const sendCode = (e: FormEvent) => {
    e.preventDefault();
    void requestCode(email.trim().toLowerCase());
  };

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'code') return;
    void run(async () => {
      markSignedIn();
      const { error } = await auth!.verifyOtp({
        email: step.email,
        token: code,
        type: 'email',
      });
      if (error) setError(t.codeError);
    });
  };

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex justify-center">
          <Logo size={28} />
        </div>
        {step.kind === 'email' ? (
          <div className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{t.signIn}</h1>
              <p className="mt-2 text-[15px] text-text-2">{t.signInHint}</p>
            </div>
            <Button
              size="lg"
              variant="secondary"
              fullWidth
              onClick={google}
              disabled={busy}
              icon={<GoogleIcon />}
            >
              {t.google}
            </Button>
            <div className="flex items-center gap-3 text-[13px] text-text-3">
              <span className="h-px flex-1 bg-border" />
              {t.or}
              <span className="h-px flex-1 bg-border" />
            </div>
            <form onSubmit={sendCode} className="flex flex-col gap-4">
              <Field label={t.email} error={error}>
                {(p) => (
                  <Input
                    {...p}
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Button type="submit" size="lg" fullWidth loading={busy}>
                {t.sendCode}
              </Button>
            </form>
            <p className="text-center text-xs text-text-3">
              {t.termsLead}{' '}
              <a className="underline" href={paths.terms}>
                {t.termsOfUse}
              </a>{' '}
              {t.and}{' '}
              <a className="underline" href={paths.privacy}>
                {t.privacyPolicy}
              </a>
              .
            </p>
          </div>
        ) : (
          <form onSubmit={verifyCode} className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{t.enterCode}</h1>
              <p className="mt-2 text-[15px] text-text-2">{t.codeSent(step.email)}</p>
            </div>
            <Field label={t.code} error={error}>
              {(p) => (
                <Input
                  {...p}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  // O comprimento do código vem do Supabase (6 por omissão, até 10); colar
                  // «123 456» ou com o texto à volta também serve.
                  pattern="[0-9]{6,10}"
                  required
                  className="text-center text-xl tracking-[0.4em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
              )}
            </Field>
            <Button type="submit" size="lg" fullWidth loading={busy}>
              {t.signIn}
            </Button>
            <div className="flex justify-center gap-4">
              <button
                type="button"
                className="text-sm text-text-2 hover:underline"
                disabled={busy}
                onClick={() => void requestCode(step.email)}
              >
                {t.resendCode}
              </button>
              <button
                type="button"
                className="text-sm text-text-2 hover:underline"
                onClick={() => {
                  setError(undefined);
                  setStep({ kind: 'email' });
                }}
              >
                {t.otherEmail}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

/** Só em desenvolvimento: entrar como uma conta fictícia do seed. */
export function DevLoginPage() {
  const [params] = useSearchParams();
  const t = authText(useLocale());
  if (!DEV_LOGIN_ENABLED) return <Navigate to="/entrar" replace />;
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-5 py-10">
      <Logo size={28} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t.devTitle}</h1>
        <p className="mt-1 text-sm text-text-2">{t.devHint}</p>
      </div>
      <ul className="flex flex-col gap-2">
        {DEV_USERS.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              className="w-full text-left"
              onClick={() => void devSignIn(u, safeNext(params.get('next')))}
            >
              <Card className="flex items-center gap-3 p-3 transition hover:border-border-strong">
                <Avatar name={u.name} />
                <span>
                  <span className="block font-medium">{u.name}</span>
                  <span className="block text-sm text-text-2">{u.email}</span>
                </span>
              </Card>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}

export function RequireSession() {
  const { session, loading } = useSession();
  const location = useLocation();
  if (loading) return <LoadingState />;
  if (!session)
    return (
      <Navigate
        to={`/entrar?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  return <Outlet />;
}
